import { API_URL } from "../constants";
import { getValidAccessToken } from "./Auth";
import { determineEnvironmentFromDomain } from "@common/utils/environment";
export interface ErrorReportPayload {
  message: string;
  stackTrace?: string;
  source?: "frontend" | "backend" | string;
  severity?: "error" | "warn" | "fatal" | "info";
  path?: string;
  environment?: string;
  userId?: string;
  userEmail?: string;
  chatId?: string;
  exchangeId?: string;
  metadata?: Record<string, any>;
}

export function getEnvironmentFromDomain(): string {
  if (typeof window === "undefined") return determineEnvironmentFromDomain();
  return determineEnvironmentFromDomain(window.location.hostname);
}

let isReportingError = false;
let reportedCount = 0;
const MAX_ERRORS_PER_MINUTE = 10;

let activeChatId: string | undefined;
let activeExchangeId: string | undefined;

export function setActiveConversationContext(ctx: { chatId?: string; exchangeId?: string }): void {
  activeChatId = ctx.chatId;
  activeExchangeId = ctx.exchangeId;
}

export function getActiveConversationContext(): { chatId?: string; exchangeId?: string } {
  return { chatId: activeChatId, exchangeId: activeExchangeId };
}

setInterval(() => {
  reportedCount = 0;
}, 60000);

export async function reportError(payload: ErrorReportPayload): Promise<boolean> {
  if (isReportingError) return false;
  if (reportedCount >= MAX_ERRORS_PER_MINUTE) return false;

  isReportingError = true;
  reportedCount++;

  try {
    const token = await getValidAccessToken();
    const environment = payload.environment || getEnvironmentFromDomain();
    const path =
      payload.path ||
      (typeof window !== "undefined" ? window.location.pathname + window.location.search : undefined);

    const metadata = { ...(payload.metadata || {}) };

    const body = {
      timestamp: new Date().toISOString(),
      source: payload.source || "frontend",
      severity: payload.severity || "error",
      message: (payload.message || "").substring(0, 2000),
      stackTrace: (payload.stackTrace || "").substring(0, 10000),
      path,
      environment,
      userId: payload.userId,
      userEmail: payload.userEmail,
      chatId: payload.chatId || activeChatId,
      exchangeId: payload.exchangeId || activeExchangeId,
      metadata,
    };

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_URL}/errors/report`, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
    });

    return response.ok;
  } catch (err) {
    console.warn("[ErrorReporter] Failed to report error to backend:", err);
    return false;
  } finally {
    isReportingError = false;
  }
}

export function initGlobalErrorListeners(): void {
  if (typeof window === "undefined") return;

  window.addEventListener("error", (event) => {
    reportError({
      message: event.message || "Uncaught window error",
      stackTrace: event.error?.stack,
      path: window.location.pathname,
      severity: "error",
      metadata: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    const message =
      reason instanceof Error ? reason.message : String(reason || "Unhandled Promise Rejection");
    const stackTrace = reason instanceof Error ? reason.stack : undefined;

    reportError({
      message: `Unhandled Rejection: ${message}`,
      stackTrace,
      path: window.location.pathname,
      severity: "error",
    });
  });
}
